import { useCallback, useEffect, useRef } from "react";

export function useSectionOrderAutosave({ pageId, setSections, updatePage, onOrderSaveError }) {
  const sectionsRef = useRef([]);
  const orderSaveTimerRef = useRef(null);
  const orderDirtyRef = useRef(false);

  useEffect(() => {
    return () => {
      if (orderSaveTimerRef.current) clearTimeout(orderSaveTimerRef.current);
    };
  }, []);

  const scheduleOrderSave = useCallback(
    (nextSections) => {
      if (!pageId) return;

      orderDirtyRef.current = true;
      sectionsRef.current = nextSections;

      if (orderSaveTimerRef.current) clearTimeout(orderSaveTimerRef.current);

      orderSaveTimerRef.current = setTimeout(async () => {
        try {
          await updatePage(pageId, { sections: sectionsRef.current });
          orderDirtyRef.current = false;
        } catch (e) {
          console.error("Order save failed:", e);
          orderDirtyRef.current = true;
          onOrderSaveError?.(e);
        }
      }, 600);
    },
    [pageId, updatePage, onOrderSaveError]
  );

  const moveSection = useCallback(
    (index, direction) => {
      setSections((prev) => {
        const next = [...prev];
        const newIndex = direction === "up" ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= next.length) return prev;

        const temp = next[index];
        next[index] = next[newIndex];
        next[newIndex] = temp;

        scheduleOrderSave(next);
        return next;
      });
    },
    [setSections, scheduleOrderSave]
  );

  const flushOrderNow = useCallback(async () => {
    if (!pageId) return;

    if (orderSaveTimerRef.current) {
      clearTimeout(orderSaveTimerRef.current);
      orderSaveTimerRef.current = null;
    }

    if (!orderDirtyRef.current) return;

    await updatePage(pageId, { sections: sectionsRef.current });
    orderDirtyRef.current = false;
  }, [pageId, updatePage]);

  return { moveSection, flushOrderNow };
}